// Column lookup + suggested-action mapping shared by every table builder.

import type { CandidateBlockData } from '../../types';
import { leaksToolNames } from '../leaksToolNames';
import type { StructuredTable } from './types';

export const colIndex = (
  columns: string[] | undefined,
  name: string
): number => {
  if (!columns) return -1;
  const lower = name.toLowerCase();
  return columns.findIndex((c) => c.toLowerCase() === lower);
};

export const cell = (
  row: string[] | undefined,
  idx: number,
  fallback = ''
): string =>
  row && idx >= 0 && idx < row.length ? (row[idx] ?? fallback) : fallback;

/** Reads a named column off a row, undefined when missing or blank. */
export const colReader =
  (columns: string[] | undefined) =>
  (row: string[], name: string): string | undefined =>
    cell(row, colIndex(columns, name)) || undefined;

// Map backend `suggested_actions` into the BlockOutput shape. Backend chooses
// both label and the prompt the chip sends, so new chips need no UI deploy.
export const mapSuggestedActions = (
  raw: StructuredTable['suggested_actions']
): CandidateBlockData['suggestedActions'] => {
  if (!raw || raw.length === 0) return undefined;
  // Old transcripts may carry chips generated before the backend's tool-name
  // filter existed — a chip prompt becomes the user's own bubble.
  raw = raw.filter(
    (a) => !leaksToolNames(a.label) && !leaksToolNames(a.prompt)
  );
  if (raw.length === 0) return undefined;
  // Explicit kinds win (payloads may mark SEVERAL chips primary — one per
  // action lane). Payloads without kinds keep the legacy rule: first is primary.
  const hasExplicitKinds = raw.some((a) => a.kind === 'primary');
  return raw.map((a, i) => ({
    id: `sa-${i}`,
    label: a.label,
    kind: (hasExplicitKinds ? a.kind === 'primary' : i === 0)
      ? ('primary' as const)
      : ('secondary' as const),
    promptTemplate: a.prompt,
  }));
};
